import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Car, 
  Plus, 
  Eye,
  Search, 
  Filter,
  Package,
  Clock,
  CheckCircle,
  Calendar,
  DollarSign,
  Truck,
  MapPin,
  X,
  Trash2,
  Image as ImageIcon,
  Download,
  Upload,
  FileSpreadsheet,
  GripVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ship,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import VehicleImportDialog from './VehicleImportDialog';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';
import { SHIP_COUNTRIES } from '../../data/shipCountries';

// Helper function to convert image paths to API endpoint
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL (OCI direct URL), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // imagePath format: "/uploads/vehicle_images/{vehicle_id}/{filename}.jpg"
  // Convert to: "{BACKEND_URL}/api/images/{vehicle_id}/{filename}.jpg"
  const match = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (match) {
    return `${API}/images/${match[1]}/${match[2]}`;
  }
  return `${BACKEND_URL}${imagePath}`; // Fallback to old format
};

const VehicleManagement = ({ user }) => {
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    make: '',
    model: '',
    year_from: '',
    year_to: '',
    purchase_date_from: '',
    purchase_date_to: '',
    etd_from: '',
    etd_to: '',
    eta_from: '',
    eta_to: '',
    release_date_from: '',
    release_date_to: ''
  });
  const [etaSortOrder, setEtaSortOrder] = useState('none'); // 'none', 'asc', 'desc'
  const [isDragging, setIsDragging] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [vehicleStatsFromAPI, setVehicleStatsFromAPI] = useState({
    total: 0,
    purchased: 0,
    shipped: 0,
    delivered: 0
  });
  const [pageSize, setPageSize] = useState(20);

  // Check if user has permission for an action
  const hasPermission = (action) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;
    
    // Check employee permissions
    if (user?.role === 'employee' && user?.permissions) {
      return user.permissions[action] === true;
    }
    
    return false;
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Drag and drop handler
  const handleDragEnd = async (result) => {
    setIsDragging(false);
    if (!result.destination) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    if (startIndex === endIndex) return;

    // Reorder the vehicles state immediately for visual feedback
    const reordered = Array.from(vehicles);
    const [moved] = reordered.splice(startIndex, 1);
    reordered.splice(endIndex, 0, moved);
    setVehicles(reordered);

    // Update the vehicles order with rate limit handling - send sequentially with small delays
    try {
      for (let index = 0; index < reordered.length; index++) {
        const vehicle = reordered[index];
        await axios.put(`${API}/vehicles/${vehicle.id}`, { 
          display_order: index 
        });
        // Small delay to avoid rate limit (60 req/min)
        if (index < reordered.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      toast.success('Vehicle order updated successfully!');
    } catch (error) {
      console.error('Error updating vehicle order:', error);
      toast.error('Failed to update vehicle order. Refresh to restore original order.');
      // Refetch to restore original order on failure
      fetchVehicles(false);
    }
  };
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]); // Array of selected vehicle IDs
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [shipToLoading, setShipToLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [formData, setFormData] = useState({
    stock_no: '',
    year: new Date().getFullYear(),
    type: '',
    model: '',
    chassis_no: '',
    auction_house: '',
    purchase_date: '',
    yard: '',
    notes: '',
    assigned_employee_id: ''
  });

  useEffect(() => {
    // Only fetch if user has permission to view vehicles
    if (hasPermission('vehicles_view')) {
      fetchVehicles();
      fetchEmployees();
    } else {
      setLoading(false);
    }
  }, []);

  // Refetch when search or filter changes (with debounce)
  useEffect(() => {
    if (!hasPermission('vehicles_view')) return;
    
    // Increased debounce to 800ms to allow users to finish typing
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search/filter change
      fetchVehicles(true, 1);
    }, 800);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, statusFilter, advancedFilters]);

  // Refetch when ETA sort order changes (no debounce needed for sorting)
  useEffect(() => {
    if (!hasPermission('vehicles_view')) return;
    
    // Reset to first page when sorting changes
    setCurrentPage(1);
    fetchVehicles(true, 1);
  }, [etaSortOrder]);

  const fetchVehicles = async (showLoading = true, page = currentPage) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Build query params with pagination
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Advanced search filters
      if (advancedFilters.make) params.append('make', advancedFilters.make);
      if (advancedFilters.model) params.append('model', advancedFilters.model);
      if (advancedFilters.year_from) params.append('year_from', advancedFilters.year_from);
      if (advancedFilters.year_to) params.append('year_to', advancedFilters.year_to);
      if (advancedFilters.purchase_date_from) params.append('purchase_date_from', advancedFilters.purchase_date_from);
      if (advancedFilters.purchase_date_to) params.append('purchase_date_to', advancedFilters.purchase_date_to);
      if (advancedFilters.etd_from) params.append('etd_from', advancedFilters.etd_from);
      if (advancedFilters.etd_to) params.append('etd_to', advancedFilters.etd_to);
      if (advancedFilters.eta_from) params.append('eta_from', advancedFilters.eta_from);
      if (advancedFilters.eta_to) params.append('eta_to', advancedFilters.eta_to);
      if (advancedFilters.release_date_from) params.append('release_date_from', advancedFilters.release_date_from);
      if (advancedFilters.release_date_to) params.append('release_date_to', advancedFilters.release_date_to);
      
      // Add sorting parameters for server-side sorting
      if (etaSortOrder !== 'none') {
        params.append('sort_by', 'eta');
        params.append('sort_order', etaSortOrder);
      }
      
      const response = await axios.get(`${API}/vehicles?${params.toString()}`);
      
      // Handle paginated response
      if (response.data.vehicles) {
        setVehicles(response.data.vehicles || []);
        setTotalVehicles(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
        setCurrentPage(response.data.page || 1);
        
        // Set vehicle stats from API if available
        if (response.data.stats) {
          setVehicleStatsFromAPI(response.data.stats);
        }
      } else {
        // Fallback for non-paginated response
        setVehicles(response.data || []);
      }
      
      // Update selectedVehicle if one is currently selected
      if (selectedVehicle) {
        const updatedVehicle = (response.data.vehicles || response.data || []).find(v => v.id === selectedVehicle.id);
        if (updatedVehicle) {
          setSelectedVehicle(updatedVehicle);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      // Only show error toast for actual errors (not permission issues or empty results)
      if (error.response) {
        if (error.response.status !== 403 && error.response.status !== 404) {
          toast.error('Failed to load vehicles');
        }
      } else if (error.request) {
        toast.error('Network error - Failed to connect');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees list');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const vehicleData = {
        ...formData,
        status: 'available', // Default status for new vehicles
        year: parseInt(formData.year)
      };

      await axios.post(`${API}/vehicles`, vehicleData);
      toast.success('Vehicle added successfully!');
      setShowAddDialog(false);
      setFormData({
        stock_no: '',
        year: new Date().getFullYear(),
        type: '',
        model: '',
        chassis_no: '',
        auction_house: '',
        purchase_date: '',
        yard: '',
        notes: '',
        assigned_employee_id: ''
      });
      fetchVehicles();
    } catch (error) {
      console.error('Error adding vehicle:', error);
      const message = extractErrorMessage(error, 'Failed to add vehicle');
      toast.error(message);
    }
  };

  const handleViewDetails = async (vehicle) => {
    try {
      // Fetch full vehicle data - list view excludes description/additional_images
      const response = await axios.get(`${API}/vehicles/${vehicle.id}`);
      setSelectedVehicle(response.data);
    } catch (error) {
      console.error('Error loading vehicle details:', error);
      setSelectedVehicle(vehicle);
    }
    setShowDetailsDialog(true);
  };

  const toggleForSale = async (vehicleId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/vehicles/${vehicleId}/list-for-sale`, 
        { for_sale: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Vehicle ${!currentStatus ? 'listed for public sale' : 'unlisted from public sale'}!`);
      fetchVehicles();
    } catch (error) {
      console.error('Error toggling for sale status:', error);
      const message = extractErrorMessage(error, 'Failed to update listing status');
      toast.error(message);
    }
  };

  // Checkbox selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedVehicles(filteredVehicles.map(v => v.id));
    } else {
      setSelectedVehicles([]);
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicles(prev => {
      if (prev.includes(vehicleId)) {
        return prev.filter(id => id !== vehicleId);
      } else {
        return [...prev, vehicleId];
      }
    });
  };

  const handleDeleteSelected = () => {
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle to delete');
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword) {
      toast.error('Please enter your admin password');
      return;
    }

    if (selectedVehicles.length === 0) {
      toast.error('No vehicles selected');
      return;
    }

    setIsDeleting(true);

    try {
      // Verify admin password first
      const verifyResponse = await axios.post(`${API}/auth/login`, {
        email: user.email,
        password: deletePassword
      });

      if (!verifyResponse.data.access_token) {
        throw new Error('Invalid password');
      }

      // Delete each selected vehicle
      let successCount = 0;
      let totalDeleted = {
        vehicles: 0,
        orders: 0,
        payments: 0,
        documents: 0
      };

      for (const vehicleId of selectedVehicles) {
        try {
          const response = await axios.delete(`${API}/vehicles/${vehicleId}`);
          successCount++;
          totalDeleted.vehicles += response.data.deleted.vehicle;
          totalDeleted.orders += response.data.deleted.orders;
          totalDeleted.payments += response.data.deleted.payments;
          totalDeleted.documents += response.data.deleted.documents;
        } catch (error) {
          console.error(`Error deleting vehicle ${vehicleId}:`, error);
        }
      }

      if (successCount > 0) {
        // Clear selections and close dialog first
        setSelectedVehicles([]);
        setShowDeleteDialog(false);
        setDeletePassword('');
        
        // Refresh the vehicle list without showing loading skeleton
        await fetchVehicles(false);
        
        // Show success message after refresh
        toast.success(`Successfully deleted ${successCount} vehicle(s)!`, {
          description: `${totalDeleted.vehicles} vehicles, ${totalDeleted.orders} orders, ${totalDeleted.payments} payments, ${totalDeleted.documents} documents removed`
        });
      } else {
        toast.error('Failed to delete any vehicles');
      }
    } catch (error) {
      console.error('Error during deletion:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid admin password. Please try again.');
      } else {
        toast.error('Failed to delete vehicles. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeletePassword('');
  };

  const handleShipTo = async (country) => {
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle to ship');
      return;
    }
    setShipToLoading(true);
    try {
      const response = await axios.post(`${API}/vehicles/ship-to`, {
        vehicle_ids: selectedVehicles,
        country
      });
      const count = response.data?.modified || response.data?.matched || selectedVehicles.length;
      const destination = SHIP_COUNTRIES.find((c) => c.slug === country);
      setSelectedVehicles([]);
      await fetchVehicles(false);
      toast.success(
        `${count} vehicle(s) listed on the ${destination?.name || country} Shipped page`
      );
    } catch (error) {
      toast.error(extractErrorMessage(error) || 'Failed to update ship-to destination');
    } finally {
      setShipToLoading(false);
    }
  };

  const handleClearShipTo = async () => {
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle');
      return;
    }
    setShipToLoading(true);
    try {
      const response = await axios.post(`${API}/vehicles/ship-to`, {
        vehicle_ids: selectedVehicles,
        country: ''
      });
      const count = response.data?.modified || response.data?.matched || selectedVehicles.length;
      setSelectedVehicles([]);
      await fetchVehicles(false);
      toast.success(`${count} vehicle(s) removed from the Shipped pages`);
    } catch (error) {
      toast.error(extractErrorMessage(error) || 'Failed to clear ship-to destination');
    } finally {
      setShipToLoading(false);
    }
  };

  const handleVehicleUpdate = async (vehicleId) => {
    try {
      // Fetch only the updated vehicle data
      const response = await axios.get(`${API}/vehicles/${vehicleId}`);
      const updatedVehicle = response.data;
      
      // Update the vehicles list with the updated vehicle
      setVehicles(prevVehicles => 
        prevVehicles.map(v => v.id === vehicleId ? updatedVehicle : v)
      );
      
      // Update the selectedVehicle if it's the one being updated
      if (selectedVehicle?.id === vehicleId) {
        setSelectedVehicle(updatedVehicle);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
      // If fetch fails, fallback to full refresh
      await fetchVehicles(false);
    }
  };

  const handleExport = async (format) => {
    try {
      // Build query params with current filters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (advancedFilters.make) params.append('make', advancedFilters.make);
      if (advancedFilters.model) params.append('model', advancedFilters.model);
      if (advancedFilters.year_from) params.append('year_from', advancedFilters.year_from);
      if (advancedFilters.year_to) params.append('year_to', advancedFilters.year_to);
      if (advancedFilters.purchase_date_from) params.append('purchase_date_from', advancedFilters.purchase_date_from);
      if (advancedFilters.purchase_date_to) params.append('purchase_date_to', advancedFilters.purchase_date_to);
      if (advancedFilters.etd_from) params.append('etd_from', advancedFilters.etd_from);
      if (advancedFilters.etd_to) params.append('etd_to', advancedFilters.etd_to);
      if (advancedFilters.eta_from) params.append('eta_from', advancedFilters.eta_from);
      if (advancedFilters.eta_to) params.append('eta_to', advancedFilters.eta_to);
      if (advancedFilters.release_date_from) params.append('release_date_from', advancedFilters.release_date_from);
      if (advancedFilters.release_date_to) params.append('release_date_to', advancedFilters.release_date_to);
      
      const url = `${API}/vehicles/export/${format}?${params.toString()}`;
      
      // Trigger download
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `vehicles_export_${timestamp}.${format}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported ${filteredVehicles.length} vehicle(s) as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting vehicles:', error);
      const message = extractErrorMessage(error, 'Failed to export vehicles');
      toast.error(message);
    }
  };

  const handleImportComplete = () => {
    fetchVehicles(false); // Refresh list without showing loading skeleton
    toast.success('Import completed! Vehicle list refreshed.');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-cyan-100 text-cyan-800';
      case 'reserved':
        return 'bg-purple-100 text-purple-800';
      case 'reserve_cancellation':
        return 'bg-red-100 text-red-800';
      case 'purchased':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'sold':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <Package className="h-4 w-4" />;
      case 'reserved':
        return <Clock className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'purchased':
        return <Package className="h-4 w-4" />;
      case 'shipped':
        return <Clock className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      case 'sold':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const handleDeleteImage = async (vehicleId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/vehicles/${vehicleId}/images?image_url=${encodeURIComponent(imageUrl)}`
      );
      toast.success('Image deleted successfully!');
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleEtaSort = () => {
    if (etaSortOrder === 'none') {
      setEtaSortOrder('asc');
    } else if (etaSortOrder === 'asc') {
      setEtaSortOrder('desc');
    } else {
      setEtaSortOrder('none');
    }
  };

  // No client-side sorting - all sorting handled by backend
  const filteredVehicles = vehicles;

  // Use stats from API (total counts across all pages) instead of current page counts
  const vehicleStats = vehicleStatsFromAPI;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  // Show permission message if user doesn't have access
  if (!hasPermission('vehicles_view')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-center">
          <Car className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h2>
          <p className="text-slate-600 max-w-md">
            You don't have permission to view vehicles. Please contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vehicle-management">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vehicle Management</h1>
          <p className="text-slate-600">Manage your vehicle inventory and track status</p>
        </div>
        <div className="flex gap-2">
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Import Button */}
          <Button variant="outline" onClick={() => setShowImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>

          {/* Add Vehicle Button - Only for users with vehicles_edit permission */}
          {hasPermission('vehicles_edit') && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-vehicle-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </Button>
              </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Vehicle</DialogTitle>
              <DialogDescription>
                Enter the vehicle details to add it to your inventory.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stock_no">Stock Number (SM)*</Label>
                  <Input
                    id="stock_no"
                    name="stock_no"
                    value={formData.stock_no}
                    onChange={handleInputChange}
                    placeholder="SM2024001"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="year">Year*</Label>
                  <Input
                    id="year"
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="2023"
                    required
                    min="1990"
                    max="2030"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Vehicle Type*</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sedan">Sedan</SelectItem>
                      <SelectItem value="SUV">SUV</SelectItem>
                      <SelectItem value="Crossover SUV">Crossover SUV</SelectItem>
                      <SelectItem value="Hatchback">Hatchback</SelectItem>
                      <SelectItem value="Station Wagon">Station Wagon</SelectItem>
                      <SelectItem value="Coupe">Coupe</SelectItem>
                      <SelectItem value="Van">Van</SelectItem>
                      <SelectItem value="Truck">Truck</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="chassis_no">Chassis Number*</Label>
                <Input
                  id="chassis_no"
                  name="chassis_no"
                  value={formData.chassis_no}
                  onChange={handleInputChange}
                  placeholder="Enter chassis number"
                  required
                />
              </div>

              <div>
                <Label htmlFor="auction_house">Auction House</Label>
                <Input
                  id="auction_house"
                  name="auction_house"
                  value={formData.auction_house}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <Input
                    id="purchase_date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="yard">Yard</Label>
                  <Input
                    id="yard"
                    name="yard"
                    value={formData.yard}
                    onChange={handleInputChange}
                    placeholder="Yard location"
                  />
                </div>
              </div>

              {/* Employee Assignment - Only show for admin */}
              {user?.role === 'admin' && (
                <div>
                  <Label htmlFor="assigned_employee_id">Assign Employee (Agent)</Label>
                  <SearchableSelect
                    value={formData.assigned_employee_id}
                    onValueChange={(value) => setFormData({...formData, assigned_employee_id: value})}
                    options={employees.map(emp => ({
                      value: emp.id,
                      label: `${emp.full_name} (${emp.email})`
                    }))}
                    placeholder="Select employee (optional)"
                    searchPlaceholder="Search employees..."
                    emptyText="No employee found."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Employee will earn commission when this vehicle is delivered
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes about the vehicle..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Add Vehicle
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Vehicles</p>
                <p className="text-2xl font-bold text-slate-900">{vehicleStats.total}</p>
              </div>
              <Car className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Purchased</p>
                <p className="text-2xl font-bold text-yellow-600">{vehicleStats.purchased}</p>
              </div>
              <Package className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Shipped</p>
                <p className="text-2xl font-bold text-blue-600">{vehicleStats.shipped}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Delivered</p>
                <p className="text-2xl font-bold text-green-600">{vehicleStats.delivered}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by chassis, model, stock number, entry date, ETA..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="reserve_cancellation">Reserve Cancellation</SelectItem>
                <SelectItem value="purchased">Purchased</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className="whitespace-nowrap"
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced Search
              {showAdvancedSearch && Object.values(advancedFilters).some(Boolean) && (
                <Badge className="ml-2 bg-blue-600">
                  {Object.values(advancedFilters).filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>

          {showAdvancedSearch && (
            <div className="mt-4 border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="adv-make">MAKE</Label>
                  <Input
                    id="adv-make"
                    value={advancedFilters.make}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, make: e.target.value.toUpperCase()})}
                    placeholder="TOYOTA"
                  />
                </div>
                <div>
                  <Label htmlFor="adv-model">MODEL</Label>
                  <Input
                    id="adv-model"
                    value={advancedFilters.model}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, model: e.target.value.toUpperCase()})}
                    placeholder="CAMRY"
                  />
                </div>
                <div>
                  <Label htmlFor="adv-year-from">YEAR (FROM)</Label>
                  <Input
                    id="adv-year-from"
                    type="number"
                    value={advancedFilters.year_from}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, year_from: e.target.value})}
                    placeholder="2015"
                  />
                </div>
                <div>
                  <Label htmlFor="adv-year-to">YEAR (TO)</Label>
                  <Input
                    id="adv-year-to"
                    type="number"
                    value={advancedFilters.year_to}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, year_to: e.target.value})}
                    placeholder="2020"
                  />
                </div>
                <div>
                  <Label htmlFor="adv-purchase-date-from">PURCHASE DATE (FROM)</Label>
                  <Input
                    id="adv-purchase-date-from"
                    type="date"
                    value={advancedFilters.purchase_date_from}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, purchase_date_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-purchase-date-to">PURCHASE DATE (TO)</Label>
                  <Input
                    id="adv-purchase-date-to"
                    type="date"
                    value={advancedFilters.purchase_date_to}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, purchase_date_to: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-etd-from">ETD (FROM)</Label>
                  <Input
                    id="adv-etd-from"
                    type="date"
                    value={advancedFilters.etd_from}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, etd_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-etd-to">ETD (TO)</Label>
                  <Input
                    id="adv-etd-to"
                    type="date"
                    value={advancedFilters.etd_to}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, etd_to: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-eta-from">ETA (FROM)</Label>
                  <Input
                    id="adv-eta-from"
                    type="date"
                    value={advancedFilters.eta_from}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, eta_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-eta-to">ETA (TO)</Label>
                  <Input
                    id="adv-eta-to"
                    type="date"
                    value={advancedFilters.eta_to}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, eta_to: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-release-date-from">RELEASE DATE (FROM)</Label>
                  <Input
                    id="adv-release-date-from"
                    type="date"
                    value={advancedFilters.release_date_from}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, release_date_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="adv-release-date-to">RELEASE DATE (TO)</Label>
                  <Input
                    id="adv-release-date-to"
                    type="date"
                    value={advancedFilters.release_date_to}
                    onChange={(e) => setAdvancedFilters({...advancedFilters, release_date_to: e.target.value})}
                  />
                </div>
              </div>
              {Object.values(advancedFilters).some(Boolean) && (
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAdvancedFilters({
                      make: '',
                      model: '',
                      year_from: '',
                      year_to: '',
                      purchase_date_from: '',
                      purchase_date_to: '',
                      etd_from: '',
                      etd_to: '',
                      eta_from: '',
                      eta_to: '',
                      release_date_from: '',
                      release_date_to: ''
                    })}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicle List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Vehicle Inventory ({filteredVehicles.length})</CardTitle>
              <CardDescription>Manage your vehicle inventory and track status</CardDescription>
            </div>
            {user?.role === 'admin' && selectedVehicles.length > 0 && (
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      disabled={shipToLoading}
                    >
                      <Ship className="h-4 w-4" />
                      Ship To
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>On the way to...</DropdownMenuLabel>
                    {SHIP_COUNTRIES.map((c) => (
                      <DropdownMenuItem key={c.slug} onClick={() => handleShipTo(c.slug)}>
                        <span className="mr-2">{c.emoji}</span>
                        {c.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleClearShipTo}>
                      <X className="h-4 w-4 mr-2" />
                      Remove from Shipped
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="destructive"
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected ({selectedVehicles.length})
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredVehicles.length > 0 ? (
            <div className="overflow-x-auto" style={{ overflowX: isDragging ? 'visible' : 'auto' }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <GripVertical className="h-4 w-4 text-slate-400" />
                    </TableHead>
                    {user?.role === 'admin' && (
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300"
                          checked={selectedVehicles.length === filteredVehicles.length && filteredVehicles.length > 0}
                          onChange={handleSelectAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>Stock ID</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Chassis No.</TableHead>
                    <TableHead>
                      <button
                        onClick={handleEtaSort}
                        className="flex items-center gap-1 hover:text-slate-900 transition-colors font-medium"
                      >
                        ETA
                        {etaSortOrder === 'none' && <ArrowUpDown className="h-4 w-4 text-slate-400" />}
                        {etaSortOrder === 'asc' && <ArrowUp className="h-4 w-4 text-blue-600" />}
                        {etaSortOrder === 'desc' && <ArrowDown className="h-4 w-4 text-blue-600" />}
                      </button>
                    </TableHead>
                    <TableHead>Entry Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Images</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                  <Droppable droppableId="vehicles">
                    {(provided, snapshot) => (
                      <TableBody 
                        {...provided.droppableProps} 
                        ref={provided.innerRef}
                        className={snapshot.isDraggingOver ? "bg-blue-50" : ""}
                      >
                        {filteredVehicles.map((vehicle, index) => (
                          <Draggable key={vehicle.id} draggableId={vehicle.id} index={index}>
                            {(provided, snapshot) => (
                              <TableRow 
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`
                                  hover:bg-slate-50 
                                  ${snapshot.isDragging ? "bg-blue-100 shadow-lg" : ""}
                                  ${selectedVehicles.includes(vehicle.id) ? "bg-blue-50" : ""}
                                `}
                              >
                                <TableCell {...provided.dragHandleProps} className="text-center">
                                  <GripVertical className="h-4 w-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                                </TableCell>
                                {user?.role === 'admin' && (
                                  <TableCell>
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded border-gray-300"
                                      checked={selectedVehicles.includes(vehicle.id)}
                                      onChange={() => handleSelectVehicle(vehicle.id)}
                                    />
                                  </TableCell>
                                )}
                      {/* Stock ID */}
                      <TableCell className="font-medium">{vehicle.stock_no}</TableCell>
                      
                      {/* Vehicle */}
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{vehicle.year} {vehicle.model}</p>
                          <p className="text-sm text-slate-500">{vehicle.type}</p>
                        </div>
                      </TableCell>
                      
                      {/* Chassis No. */}
                      <TableCell className="font-mono text-sm">{vehicle.chassis_no}</TableCell>
                      
                      {/* ETA */}
                      <TableCell>
                        <div>
                          {vehicle.eta ? (
                            <p className="font-medium text-slate-900">{vehicle.eta}</p>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      
                      {/* Entry Date */}
                      <TableCell>
                        <div>
                          {vehicle.entry_date ? (
                            <p className="font-medium text-slate-900">{new Date(vehicle.entry_date).toLocaleDateString()}</p>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <Badge className={`${getStatusColor(vehicle.status)} flex items-center space-x-1 w-fit`}>
                          {getStatusIcon(vehicle.status)}
                          <span className="capitalize">{vehicle.status}</span>
                        </Badge>
                      </TableCell>
                      
                      {/* Images */}
                      <TableCell>
                        {vehicle.images && vehicle.images.length > 0 ? (
                          <div className="relative">
                            <img
                              src={getImageUrl(vehicle.images[0])}
                              alt={`${vehicle.year} ${vehicle.model}`}
                              className="w-16 h-16 object-cover rounded border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-16 h-16 items-center justify-center bg-slate-100 rounded border text-slate-400">
                              <ImageIcon className="h-6 w-6" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-16 h-16 bg-slate-100 rounded border text-slate-400">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Actions */}
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(vehicle)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                          {user?.role !== 'customer' && (
                            <Button
                              variant={vehicle.for_sale ? "default" : "outline"}
                              size="sm"
                              onClick={() => toggleForSale(vehicle.id, vehicle.for_sale)}
                              className={vehicle.for_sale ? "bg-green-600 hover:bg-green-700" : ""}
                            >
                              {vehicle.for_sale ? '✓ Listed' : 'List for Sale'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Car className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Vehicles Found</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm || statusFilter !== 'all' ? 'No vehicles match your search criteria.' : 'Start by adding your first vehicle to the inventory.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button 
                  onClick={() => setShowAddDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Vehicle
                </Button>
              )}
            </div>
          )}
          
          {/* Pagination Controls */}
          {!loading && vehicles.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalVehicles)} of {totalVehicles} vehicles
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    fetchVehicles(true, newPage);
                  }}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setCurrentPage(pageNum);
                          fetchVehicles(true, pageNum);
                        }}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    fetchVehicles(true, newPage);
                  }}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicle Details Dialog */}
      {selectedVehicle && (
        <VehicleDetailsDialog
          vehicle={selectedVehicle}
          isOpen={showDetailsDialog}
          onClose={() => setShowDetailsDialog(false)}
          onUpdate={() => handleVehicleUpdate(selectedVehicle.id)}
          user={user}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete {selectedVehicles.length} Vehicle{selectedVehicles.length > 1 ? 's' : ''} Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-red-800 font-medium mb-3">⚠️ This will permanently delete:</p>
                <ul className="text-sm text-red-700 space-y-1 ml-4">
                  <li>• {selectedVehicles.length} vehicle{selectedVehicles.length > 1 ? 's' : ''} from inventory</li>
                  <li>• All orders associated with these vehicles</li>
                  <li>• All payment records for those orders</li>
                  <li>• All documents linked to these vehicles</li>
                </ul>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border max-h-40 overflow-y-auto">
                <p className="font-medium text-slate-900 mb-2">Selected Vehicles:</p>
                <div className="space-y-1">
                  {selectedVehicles.map(vehicleId => {
                    const vehicle = vehicles.find(v => v.id === vehicleId);
                    return vehicle ? (
                      <div key={vehicleId} className="text-sm text-slate-700">
                        • {vehicle.stock_no} - {vehicle.year} {vehicle.brand} {vehicle.model}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-password" className="text-slate-900 font-medium">
                  Enter your admin password to confirm:
                </Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Admin password"
                  className="border-red-300 focus:border-red-500 focus:ring-red-500"
                  disabled={isDeleting}
                />
                <p className="text-xs text-slate-600">
                  This action cannot be undone. Please enter your password to proceed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting || !deletePassword}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <VehicleImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default VehicleManagement;